import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, filter, switchMap } from 'rxjs';
import { futureGamesHeader, PageLayoutComponent, TitileNavbarComponent } from '@ui-components';
import { COPY_TOKEN, CopyProvider } from '@core';
import { IonicModule } from '@ionic/angular';
import { GetGamesForPostQueryHandler, GetKnownCitiesQueryHandler } from '../../application/handlers';

@Component({
  selector: 'lib-generate-post',
  templateUrl: './generate-post.component.html',
  styleUrls: ['./generate-post.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TitileNavbarComponent, IonicModule, PageLayoutComponent],
  providers: [GetGamesForPostQueryHandler, GetKnownCitiesQueryHandler]
})
export class GeneratePostComponent {
  private readonly getGamesForPostQueryHandler: GetGamesForPostQueryHandler = inject(GetGamesForPostQueryHandler);
  private readonly getKnownCitiesQueryHandler: GetKnownCitiesQueryHandler = inject(GetKnownCitiesQueryHandler);
  private readonly copyProvider: CopyProvider = inject(COPY_TOKEN);

  readonly endDays: WritableSignal<number | null> = signal<number | null>(null);
  readonly city: WritableSignal<string | null> = signal<string | null>(null);
  readonly radius: WritableSignal<number | null> = signal<number | null>(null);

  readonly games: Signal<string[] | undefined> = toSignal(
    combineLatest([toObservable(this.endDays), toObservable(this.city), toObservable(this.radius)]).pipe(
      filter(
        ([endDays, city, radius]: [number | null, string | null, number | null]) =>
          endDays !== null && city !== null && radius !== null
      ),
      switchMap(([endDays, city, radius]: [number | null, string | null, number | null]) =>
        this.getGamesForPostQueryHandler.query(endDays!, city!, radius!)
      )
    )
  );

  readonly availableCities: Signal<string[] | undefined> = toSignal(this.getKnownCitiesQueryHandler.query());

  readonly availableDays: number[] = [1, 2, 3, 4, 5, 6, 7, 14];

  readonly availableRadiuses: number[] = [5, 10, 15, 20, 30, 50, 70, 100];

  readonly gamesCount: Signal<number> = computed(() => this.games()?.length ?? 0);

  copy(): void {
    const gamesData: string[] | undefined = this.games();
    if (gamesData?.length) {
      this.copyProvider.copyToClipboard(`${futureGamesHeader()}${gamesData.join('\n')}`).subscribe();
    }
  }

  setCity(event: CustomEvent): void {
    this.city.set(event.detail.value);
  }

  setDay(event: CustomEvent): void {
    this.endDays.set(event.detail.value);
  }

  setRadius(event: CustomEvent): void {
    this.radius.set(event.detail.value);
  }
}
